/**
 * Date Preset Menu Component
 * Provides predefined date ranges + custom date range picker
 * Based on kiosk-sessions implementation per HIVE-6245
 */

import { useState } from 'react';
import { CalendarIcon, ChevronLeft } from 'lucide-react';
import { startOfWeek, endOfWeek, subDays, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, format } from 'date-fns';
import { cn } from '@/core/lib/utils';
import { Button } from '@/core/components/ui/button';
import { Calendar } from '@/core/components/ui/calendar';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { toApiDateFormat, fromApiDateFormat, getDateRangeDays, MAX_SESSION_REPORT_RANGE_DAYS } from '../../utils/date-utils';

export type DatePresetOption =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'last10Days'
  | 'thisMonth'
  | 'last30Days'
  | 'lastMonth'
  | 'last60Days'
  | 'thisQuarter'
  | 'custom';

interface DatePresetMenuProps {
  value: {
    from: string; // YYYY-MM-DD format
    to: string;   // YYYY-MM-DD format
  };
  onChange: (range: { from: string; to: string }) => void;
  className?: string;
}

const presetLabels: Record<DatePresetOption, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  last10Days: 'Last 10 Days',
  thisMonth: 'This Month',
  last30Days: 'Last 30 Days',
  lastMonth: 'Last Month',
  last60Days: 'Last 60 Days',
  thisQuarter: 'This Quarter',
  custom: 'Custom'
};

export function DatePresetMenu({ value, onChange, className }: DatePresetMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<DatePresetOption>('custom');
  const [tempRange, setTempRange] = useState<{ from?: Date; to?: Date }>({});
  const [clickStep, setClickStep] = useState<'from' | 'to'>('from');
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');

  const calculateDateRange = (preset: DatePresetOption): { from: Date; to: Date } | null => {
    const now = new Date();
    let from: Date;
    let to: Date;

    switch (preset) {
      case 'today':
        from = new Date(now);
        to = new Date(now);
        break;

      case 'yesterday': {
        const yesterday = subDays(now, 1);
        from = new Date(yesterday);
        to = new Date(yesterday);
        break;
      }

      case 'thisWeek':
        from = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        to = endOfWeek(now, { weekStartsOn: 1 }); // Sunday
        break;

      case 'lastWeek': {
        const lastWeek = subDays(now, 7);
        from = startOfWeek(lastWeek, { weekStartsOn: 1 });
        to = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      }

      case 'last10Days':
        from = subDays(now, 9); // Today + 9 days ago = 10 days
        to = new Date(now);
        break;

      case 'thisMonth':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;

      case 'last30Days':
        from = subDays(now, 29); // Today + 29 days ago = 30 days
        to = new Date(now);
        break;

      case 'lastMonth': {
        const lastMonth = subMonths(now, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
      }

      case 'last60Days':
        from = subDays(now, 59); // Today + 59 days ago = 60 days
        to = new Date(now);
        break;

      case 'thisQuarter':
        from = startOfQuarter(now);
        to = endOfQuarter(now);
        break;

      case 'custom':
        return null;

      default:
        return null;
    }

    return { from, to };
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Initialize temp range from current values
      const fromDate = fromApiDateFormat(value.from);
      const toDate = fromApiDateFormat(value.to);
      setTempRange({ from: fromDate, to: toDate });
      setFromInput(value.from);
      setToInput(value.to);
      // Reset to preset menu view
      setShowCustomPicker(false);
    }
  };

  const handlePresetSelect = (preset: DatePresetOption) => {
    if (preset === 'custom') {
      setShowCustomPicker(true);
      setClickStep('from'); // Reset to first click for fresh selection
      setTempRange({}); // Clear previous selections
      setFromInput('');
      setToInput('');
      return;
    }

    const range = calculateDateRange(preset);
    if (range) {
      setSelectedPreset(preset);
      onChange({
        from: toApiDateFormat(range.from),
        to: toApiDateFormat(range.to)
      });
      setIsOpen(false);
    }
  };

  const handleBackToPresets = () => {
    setShowCustomPicker(false);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    // If both dates are already selected, start a new selection
    if (tempRange.from && tempRange.to) {
      // New selection: set this as the new start date
      setTempRange({ from: date, to: undefined });
      setFromInput(toApiDateFormat(date));
      setToInput('');
      setClickStep('to');
      return;
    }

    if (clickStep === 'from') {
      // First click: set start date
      setTempRange({ from: date, to: undefined });
      setFromInput(toApiDateFormat(date));
      setToInput(''); // Clear end date
      setClickStep('to'); // Move to selecting end date
    } else {
      // Second click: set end date
      // Validate that end date is not before start date
      if (tempRange.from && date >= tempRange.from) {
        setTempRange(prev => ({ ...prev, to: date }));
        setToInput(toApiDateFormat(date));
      } else if (tempRange.from && date < tempRange.from) {
        // If user clicks date before start, swap them
        setTempRange({ from: date, to: tempRange.from });
        setFromInput(toApiDateFormat(date));
        setToInput(toApiDateFormat(tempRange.from));
      }
    }
  };

  const handleFromInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFromInput(newValue);

    // Try to parse as YYYY-MM-DD
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(newValue)) {
      try {
        const date = fromApiDateFormat(newValue);
        setTempRange(prev => ({ ...prev, from: date }));
      } catch {
        // Invalid date, keep input but don't update temp range
      }
    }
  };

  const handleToInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setToInput(newValue);

    // Try to parse as YYYY-MM-DD
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(newValue)) {
      try {
        const date = fromApiDateFormat(newValue);
        setTempRange(prev => ({ ...prev, to: date }));
      } catch {
        // Invalid date, keep input but don't update temp range
      }
    }
  };

  const rangeExceedsMaxDays = Boolean(
    tempRange.from && tempRange.to &&
    getDateRangeDays(tempRange.from, tempRange.to) > MAX_SESSION_REPORT_RANGE_DAYS
  );

  const handleApplyCustom = () => {
    if (tempRange.from && tempRange.to && !rangeExceedsMaxDays) {
      setSelectedPreset('custom');
      onChange({
        from: toApiDateFormat(tempRange.from),
        to: toApiDateFormat(tempRange.to)
      });
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleCancelCustom = () => {
    setShowCustomPicker(false);
  };

  const displayText = selectedPreset !== 'custom'
    ? presetLabels[selectedPreset]
    : `${format(fromApiDateFormat(value.from), 'dd-MMM-yyyy')} - ${format(fromApiDateFormat(value.to), 'dd-MMM-yyyy')}`;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-xs text-muted-foreground">Period</span>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id="date-preset"
            variant="outline"
            size="sm"
            className={cn(
              'h-8 w-[260px] justify-start text-left font-normal border-[#E8E8E8]'
            )}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {displayText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {!showCustomPicker ? (
            // Preset Menu
            <div className="w-[200px] p-2">
              <div className="flex flex-col">
                {(Object.keys(presetLabels) as DatePresetOption[]).map((preset) => (
                  <Button
                    key={preset}
                    variant={selectedPreset === preset ? "secondary" : "ghost"}
                    className={cn(
                      "justify-start font-normal h-9",
                      selectedPreset === preset && "bg-secondary"
                    )}
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {presetLabels[preset]}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            // Custom Date Picker
            <div className="w-auto">
              {/* Back button */}
              <div className="p-3 border-b flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToPresets}
                  className="h-8 px-2"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <span className="text-sm font-medium">Custom Date Range</span>
              </div>

              {/* Custom date inputs */}
              <div className="p-4 border-b space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from-date" className="text-sm">From Date</Label>
                    <Input
                      id="from-date"
                      type="date"
                      value={fromInput}
                      onChange={handleFromInputChange}
                      className="w-full placeholder:text-gray-400"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="to-date" className="text-sm">To Date</Label>
                    <Input
                      id="to-date"
                      type="date"
                      value={toInput}
                      onChange={handleToInputChange}
                      className="w-full placeholder:text-gray-400"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </div>

              {/* Click step indicator */}
              <div className="px-4 py-2 bg-muted/50 border-b">
                <p className="text-sm font-medium">
                  {tempRange.from && tempRange.to
                    ? 'Click to select new Start Date'
                    : clickStep === 'from'
                      ? 'Step 1: Select Start Date'
                      : 'Step 2: Select End Date'}
                </p>
                {tempRange.from && !tempRange.to && clickStep === 'to' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    From: {format(tempRange.from, 'dd-MMM-yyyy')}
                  </p>
                )}
                {tempRange.from && tempRange.to && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Current: {format(tempRange.from, 'dd-MMM-yyyy')} - {format(tempRange.to, 'dd-MMM-yyyy')}
                  </p>
                )}
                {rangeExceedsMaxDays && (
                  <p className="text-xs text-red-500 mt-1">
                    Range too large ({getDateRangeDays(tempRange.from!, tempRange.to!)} days). Please select {MAX_SESSION_REPORT_RANGE_DAYS} days or less.
                  </p>
                )}
              </div>

              {/* Calendar */}
              <Calendar
                initialFocus
                mode="single"
                defaultMonth={tempRange.from}
                selected={clickStep === 'from' ? tempRange.from : tempRange.to}
                onSelect={handleDateSelect}
                numberOfMonths={2}
                modifiers={{
                  range_start: tempRange.from ? [tempRange.from] : [],
                  range_end: tempRange.to ? [tempRange.to] : [],
                  range_middle: tempRange.from && tempRange.to
                    ? { after: tempRange.from, before: tempRange.to }
                    : []
                }}
                modifiersClassNames={{
                  range_start: 'bg-primary text-primary-foreground rounded-l-md',
                  range_end: 'bg-primary text-primary-foreground rounded-r-md',
                  range_middle: 'bg-accent rounded-none'
                }}
              />

              {/* Action buttons */}
              <div className="p-3 border-t flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelCustom}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyCustom}
                  disabled={!tempRange.from || !tempRange.to || rangeExceedsMaxDays}
                  className="bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
